import { useState, useEffect, useRef, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import { toast } from 'react-toastify'
import Server from "../../Shared/Server"
import Spinner from '../../Shared/Spinner'
import { categories_list, months_list, years_list } from "../../Shared/List";
import SelectField from "../../Shared/SelectField"
import { FaFileExport } from "react-icons/fa"
import {AgGridReact} from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

const MonthlyReportSummary = (props) => {
    const current_user = JSON.parse(localStorage.getItem('user'))
    const billing_date  = current_user?.branch?.billing_date

    const [errors, setErrors] = useState({});
    const [data, setData] = useState([]);
    const [pinnedBottomRowData, setPinnedBottomRowData] = useState([]);
    const [title, setTitle] = useState('');
    const [byOption, setByOption] = useState([]);
    const [yearOption, setYearByOption] = useState([]);
    const [monthOption, setMonthByOption] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const gridRef = useRef();
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        year: billing_date.year,
        month: billing_date.month,
        by: 1,
    });
    const {year, month, by} = formData;

    const fetchData = async (payload) => {
        setTitle(`${months_list[formData.month - 1].name} ${year} Bill Progress by ${categories_list[formData.by - 1].name}`)
        setIsLoading(true)
        try {
            const response = await Server.post('/reports/monthly-report', payload)
            const categories = []
            const years = []
            const months = []

            categories_list.map(category => (
                categories.push({
                    id: category.id,
                    key: category.key,
                    name: category.name
                })
            ))

            years_list.map(year => (
                years.push({
                    id: year.id,
                    key: year.key,
                    name: year.name
                })
            ))

            months_list.map(month => (
                months.push({
                    id: month.id,
                    key: month.key,
                    name: month.name
                })
            ))

            setData(response?.data)
            setByOption(categories)
            setYearByOption(years)
            setMonthByOption(months)
            setIsLoading(false)

            const output = response?.data.reduce((data, row) => {
                data.previous_balance += parseFloat(row.previous_balance);
                data.debit += parseFloat(row.debit);
                data.credit += parseFloat(row.credit);
                data.discount += parseFloat(row.discount);
                data.total += parseFloat(row.total);
                return data;
            }, {previous_balance: 0.00, debit: 0.00, credit: 0.00, total: 0.00, paid: 0.00, discount: 0.00, balance: 0.00, progress: '0%'});
            const total = output.total && output.total > 0 ? output.total : 1;
            const progress = ((output.credit + output.discount)/total * 100).toFixed(2);
            setPinnedBottomRowData([
                {
                    previous_balance: parseFloat(output.previous_balance).toFixed(2),
                    debit: parseFloat(output.debit).toFixed(2),
                    total: parseFloat(output.total).toFixed(2),
                    credit: parseFloat(output.credit).toFixed(2),
                    discount: parseFloat(output.discount).toFixed(2),
                    balance: parseFloat(output.total - (output.credit + output.discount)).toFixed(2),
                    progress: `${progress}%`,
                }
            ]);
        } catch (error) {
            setIsLoading(false)
            if(error?.response?.status === 401){
                localStorage.removeItem('user')
                navigate('/login')
            }else if (error?.response?.status === 422) {
                setErrors({})
                error.response?.data?.detail.map((err) => (
                    setErrors((prevState) => ({
                        ...prevState,
                        [err.loc[1]]: err.msg
                    }))
                ))
            }else if (error?.response?.status === 403){
                toast.error(error?.response?.data?.detail || 'Forbidden');
                navigate('/');
            }else {
                toast.error(error?.response?.data?.detail || 'Failed to load monthly report')
            }
        }
    }

    useEffect(() => {

        //find category name with its index
        const by = categories_list[formData.by - 1].name
        fetchData({year, month, by})
    }, [])

    const onChange = (e) => {
        setFormData((prevState) => ({
            ...prevState,
            [e.target.name]: e.target.value
        }))

    }

    const onSubmit = (e) => {
        e.preventDefault();
        //find category name with its index
        const by = categories_list[formData.by - 1].name
        const payload = {year, month, by}
        fetchData(payload)

    }

    const columnDefs = useMemo(() => [
        {
            headerName: "#",
            filter: false,
            maxWidth: 70,
            sortable: true,
            valueFormatter: function (params) {
            if (isNaN(params.node.id)) {
                return "";
            }
            return `${Number(params.node.rowIndex) + 1}`;
            },
        },

        {
            headerName: "Name",
            field: "name",
            sortable: true,
            filter: true,
        },
        {
            headerName: "Previous Balance",
            field: "previous_balance",
            sortable: true,
            filter: 'agNumberColumnFilter',
        },
        {
            headerName: "Bill",
            field: "debit",
            sortable: true,
            filter: 'agNumberColumnFilter',
        },
        {
            headerName: "Total",
            field: "total",
            sortable: true,
            filter: 'agNumberColumnFilter',
        },
        {
            headerName: "Paid",
            field: "credit",
            sortable: true,
            filter: 'agNumberColumnFilter',
        },
        {
            headerName: "Discount",
            field: "discount",
            sortable: true,
            filter: 'agNumberColumnFilter',
        },
        {
            headerName: "Balance",
            field: "balance",
            sortable: true,
            filter: 'agNumberColumnFilter',
        },
        {
            headerName: "Progress",
            field: "progress",
            sortable: true,
            filter: true,
        },
    ], []);

    let gridApi
    const onGridReady=(params)=>{
        gridApi=params.api
    }

    const onExportClick=()=>{
        gridApi.exportDataAsCsv({ fileName: `${title}.csv` })
    }

    const getRowStyle = (params) => {
        if (params.node.rowPinned) {
          return {
            backgroundColor: '#f8f9fa',
            fontWeight: 'bold',
            color: '#000',
          };
        }
        return {};
    };

    const defaultColDef = useMemo(() => {
        return {
            filterParams: {
                buttons: []
            }
        };
    }, []);

    const onFirstDataRendered = (params) => {
        params.api.autoSizeAllColumns();
    }

    const onGridSizeChanged = (params) => {
        params.api.autoSizeAllColumns();
    }

    if(isLoading){
        return <Spinner/>
    }

  return (
    <>
        <form onSubmit={onSubmit}>
            <div className="row">
                <div className="col-md-2">
                    <SelectField
                        name="year"
                        value={parseInt(year)}
                        options={yearOption}
                        onChange={onChange}
                        error={errors.year}
                    />
                </div>
                <div className="col-md-2">
                    <SelectField
                        name="month"
                        value={parseInt(month)}
                        options={monthOption}
                        onChange={onChange}
                        error={errors.month}
                    />
                </div>
                <div className="col-md-2">
                    <SelectField
                        name="by"
                        value={parseInt(by)}
                        options={byOption}
                        onChange={onChange}
                        error={errors.by}
                    />
                </div>
                <div className="col-md-4">
                    <input type="submit" className="btn btn-info mt-1" value="Apply Filter" style={{float: 'left'}}/>
                </div>
                <div className="col-md-2">
                    <button onClick={()=>onExportClick()} className="btn btn-success mb-2" style={{float: 'right'}}><FaFileExport/> &nbsp;Export</button>
                </div>
            </div>
            <br/>
        </form>

        <div className="card mt-0">
            <div className="card-header p-2 bg-info text-white d-flex justify-content-center">
                    <h3 className="card-title">{title}</h3>
            </div>
            <div className="card-body">
                <div className="row">
                    <div className="col-md-12">
                        <div className="ag-theme-alpine responsive-aggrid" style={{ height: 400, width: '100%' }}>
                        <AgGridReact
                            suppressMenuHide={true}
                            onFirstDataRendered={onFirstDataRendered}
                            onGridSizeChanged={onGridSizeChanged}
                            defaultColDef={defaultColDef}
                            rowData={data}
                            domLayout={"normal"}
                            rowSelection={"multiple"}

                            animateRows={true}

                            pagination={false}
                            resizable={true}
                            enableCellTextSelection={true}
                            suppressCellFocus={true}


                            ref={gridRef}
                            onGridReady={onGridReady}
                            getRowStyle={getRowStyle}
                            pinnedBottomRowData={pinnedBottomRowData}
                            columnDefs={columnDefs}
                        ></AgGridReact>{" "}
                    </div>
                    </div>
                </div>
            </div>
        </div>
      <br/>
    </>
  )
}

export default MonthlyReportSummary